/**
 * Beslutsläge API Service
 * 
 * Handles Stripe checkout and case management for Premium features.
 * 
 * Flow:
 * 1. User clicks "Aktivera Beslutsläge"
 * 2. Frontend sends calculation data to backend
 * 3. Backend creates Stripe checkout session (FIXED price)
 * 4. User completes payment on Stripe
 * 5. Redirect back with session_id
 * 6. Frontend polls for payment status
 * 7. Premium content unlocked
 */

import { API_BASE_URL } from './apiBase';
import { SALES_ENABLED, SalesDisabledError } from './featureFlags';

const API_URL = API_BASE_URL;

/**
 * Get auth headers
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Create checkout session for Beslutsläge Premium
 * @param {string} productType - e.g., "fastighetsvarde"
 * @param {object} calculationData - { inputs, result }
 * @returns {Promise<{checkout_url, session_id, case_id}>}
 */
export const createBeslutslageCheckout = async (productType, calculationData) => {
  // Wartemodus: hard chokepoint. No /api/payments/create-checkout or
  // /api/beslutslage/checkout call may happen while SALES_ENABLED is false,
  // regardless of which UI path triggered this function.
  if (!SALES_ENABLED) {
    throw new SalesDisabledError();
  }

  const originUrl = window.location.origin;
  
  const response = await fetch(`${API_URL}/api/beslutslage/checkout`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      product_type: productType,
      calculation_data: calculationData,
      origin_url: originUrl
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create checkout');
  }
  
  return response.json();
};

/**
 * Poll checkout status after returning from Stripe
 * @param {string} sessionId - Stripe session ID
 * @returns {Promise<{case_id, payment_status, premium_unlocked}>}
 */
export const pollCheckoutStatus = async (sessionId) => {
  const response = await fetch(`${API_URL}/api/beslutslage/checkout/status/${sessionId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to check status');
  }
  
  return response.json();
};

/**
 * Get a saved Beslutsläge case
 * @param {string} caseId
 * @returns {Promise<BeslutslageCaseResponse>}
 */
export const getBeslutslageCase = async (caseId) => {
  const response = await fetch(`${API_URL}/api/beslutslage/case/${caseId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get case');
  }
  
  return response.json();
};

/**
 * Get all my Beslutsläge cases
 * @returns {Promise<Array>}
 */
export const getMyBeslutslageCases = async () => {
  const response = await fetch(`${API_URL}/api/beslutslage/my-cases`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get cases');
  }
  
  return response.json();
};

/**
 * Refresh payment status for a case
 * @param {string} caseId
 * @returns {Promise<{case_id, payment_status, premium_unlocked, message}>}
 */
export const refreshCaseStatus = async (caseId) => {
  const response = await fetch(`${API_URL}/api/beslutslage/case/${caseId}/refresh-status`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to refresh status');
  }
  
  return response.json();
};

/**
 * Check URL params for Beslutsläge return from Stripe
 * @returns {{success: boolean, cancelled: boolean, caseId: string|null, sessionId: string|null}}
 */
export const checkBeslutslageReturn = () => {
  const params = new URLSearchParams(window.location.search);
  
  return {
    success: params.get('beslutslage_success') === 'true',
    cancelled: params.get('beslutslage_cancelled') === 'true',
    caseId: params.get('case_id'),
    sessionId: params.get('session_id')
  };
};

/**
 * Clear Beslutsläge URL params
 */
export const clearBeslutslageParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('beslutslage_success');
  url.searchParams.delete('beslutslage_cancelled');
  url.searchParams.delete('case_id');
  url.searchParams.delete('session_id');
  window.history.replaceState({}, '', url.toString());
};

export default {
  createBeslutslageCheckout,
  pollCheckoutStatus,
  getBeslutslageCase,
  getMyBeslutslageCases,
  checkBeslutslageReturn,
  clearBeslutslageParams
};
